import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Avatar,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  SelectChangeEvent,
  Button,
  TextField,
  CircularProgress
} from '@mui/material';
import {
  Description as DocumentIcon,
  HourglassTop as AwaitingIcon,
  CheckCircle as CompletedIcon,
  Groups as TotalOrdersIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import moment from 'moment';
import { DateInputField } from 'components';
import { getBottleOrderDetailsForAnalyticReport, downloadBottleAnalyticsReport, getTATDashboardDetails, getInternalUserDespatchLocations } from 'services';
import { DashboardBarChartCard } from 'modules/quote/Dashboard/components/DashboardBarChartCard';
import { DashboardPieChartCard } from 'modules/quote/Dashboard/components/DashboardPieChartCard';
import { ClientMultiSelect, ClientOption } from 'modules/quote/Dashboard/components/ClientMultiSelect';

const toClientIds = (value: string) => value.split(',').map((id) => id.trim()).filter(Boolean);

interface BottleOrderItem {
  id: number;
  orderName?: string;
  orderBy?: number;
  orderByName?: string;
  userType?: number;
  clientID?: number;
  clientName?: string;
  orderedOn?: string;
  requiredDate?: string;
  status?: number;
  statusDescription?: string;
  despatchLocationID?: number;
  despatchLocation?: string;
}

const bottleStatusOptions = [
  { id: 1, name: 'Requested' },
  { id: 2, name: 'Awaiting Fulfilment' },
  { id: 3, name: 'Awaiting Dispatch' },
  { id: 4, name: 'Completed' },
];

interface StatCardProps {
  title: string;
  count: number;
  color: string;
}

const getStatCardIcon = (title: string) => {
  const normalizedTitle = String(title || '').trim().toLowerCase();

  if (normalizedTitle === 'requested' || normalizedTitle === 'awaitingfulfilment') {
    return <AwaitingIcon sx={{ fontSize: 30 }} />;
  }

  if (normalizedTitle === 'completed') {
    return <CompletedIcon sx={{ fontSize: 30 }} />;
  }

  if (normalizedTitle === 'total bottle orders') {
    return <TotalOrdersIcon sx={{ fontSize: 30 }} />;
  }

  return <DocumentIcon sx={{ fontSize: 30 }} />;
};

const StatCard: React.FC<StatCardProps> = ({ title, count, color }) => (
  <Card sx={{ height: '100%', position: 'relative', overflow: 'visible' }}>
    <CardContent sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
            {count.toLocaleString()}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
        </Box>
        <Avatar
          sx={{
            bgcolor: color,
            width: 56,
            height: 56,
            boxShadow: '0 4px 14px 0 rgba(0,0,0,0.15)'
          }}
        >
          {getStatCardIcon(title)}
        </Avatar>
      </Box>
    </CardContent>
  </Card>
);

export function BottleOrderPage() {
  const today = moment().startOf('day').format('YYYY-MM-DD');
  const defaultFromDate = moment().startOf('day').subtract(6, 'days').format('YYYY-MM-DD');

  const initialFormData = {
    fromDate: defaultFromDate,
    toDate: today,
    clientName: '',
    status: '',
    despatchLocation: '',
    orderedBy: ''
  };

  const [formData, setFormData] = useState(initialFormData);
  const [bottleOrders, setBottleOrders] = useState<BottleOrderItem[]>([]);
  const [hasFetchedData, setHasFetchedData] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const isSearchingRef = useRef(false);
  const [tatChartData, setTatChartData] = useState<Array<{ despatchLocation: string; tat: number }>>([]);
  const [despatchLocations, setDespatchLocations] = useState<any[]>([]);
  const [selectedClients, setSelectedClients] = useState<ClientOption[]>([]);

  const handleClientChange = useCallback((clients: ClientOption[]) => {
    setSelectedClients(clients);
    setFormData((prev) => ({ ...prev, clientName: clients.map((client) => client.id).join(',') }));
  }, []);

  const extractDownloadFileName = (contentDisposition?: string) => {
    if (!contentDisposition) {
      return 'BottleOrders.xlsx';
    }

    const utf8NameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8NameMatch?.[1]) {
      return decodeURIComponent(utf8NameMatch[1].replace(/\"/g, '').trim());
    }

    const plainNameMatch = contentDisposition.match(/filename=([^;]+)/i);
    if (plainNameMatch?.[1]) {
      return plainNameMatch[1].replace(/\"/g, '').trim();
    }

    return 'BottleOrders.xlsx';
  };

  const triggerFileDownload = (response: any) => {
    if (!response?.data) {
      return;
    }

    const contentType = response?.headers?.['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    const contentDisposition = response?.headers?.['content-disposition'];
    const fileName = extractDownloadFileName(contentDisposition);

    const blob = response.data instanceof Blob ? response.data : new Blob([response.data], { type: contentType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const fetchBottlerData = async (filters: typeof formData, type: 'download' | 'view' = 'view') => {
    if (type === 'download') {
      const effectiveFromDate = filters.fromDate || defaultFromDate;
      const effectiveToDate = filters.toDate || today;
      const apiFromDate = moment(effectiveFromDate, 'YYYY-MM-DD', true).format('YYYY-MM-DD');
      const apiToDate = moment(effectiveToDate, 'YYYY-MM-DD', true).format('YYYY-MM-DD');

      await downloadBottleAnalyticsReport(
        apiFromDate,
        apiToDate,
        filters.orderedBy !== '' ? filters.orderedBy : null,
        filters.status !== '' ? filters.status : null,
        filters.despatchLocation !== '' ? filters.despatchLocation : null,
        toClientIds(filters.clientName)
      );
      return;
    }

    const effectiveFromDate = filters.fromDate || defaultFromDate;
    const effectiveToDate = filters.toDate || today;
    const apiFromDate = moment(effectiveFromDate, 'YYYY-MM-DD', true).format('YYYY-MM-DD');
    const apiToDate = moment(effectiveToDate, 'YYYY-MM-DD', true).format('YYYY-MM-DD');

    setIsLoading(true);

    try {
      const response = await getBottleOrderDetailsForAnalyticReport(
        apiFromDate,
        apiToDate,
        filters.orderedBy !== '' ? filters.orderedBy : null,
        filters.status !== '' ? filters.status : null,
        filters.despatchLocation !== '' ? filters.despatchLocation : null,
        toClientIds(filters.clientName)
      );

      if (response?.data) {
        setBottleOrders(Array.isArray(response.data) ? response.data : []);
      } else {
        setBottleOrders([]);
      }
      setHasFetchedData(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBottlerData(initialFormData, 'view');
    fetchTATDashboardData(initialFormData);
    getInternalUserDespatchLocations().then((response: any) => {
      setDespatchLocations(response?.status === 200 ? response.data || [] : []);
    }).catch(() => setDespatchLocations([]));
  }, []);

  const handleDownloadReport = async () => {
    const effectiveFromDate = formData.fromDate || defaultFromDate;
    const effectiveToDate = formData.toDate || today;
    const apiFromDate = moment(effectiveFromDate, 'YYYY-MM-DD', true).format('YYYY-MM-DD');
    const apiToDate = moment(effectiveToDate, 'YYYY-MM-DD', true).format('YYYY-MM-DD');
    const response = await downloadBottleAnalyticsReport(
      apiFromDate,
      apiToDate,
      formData.orderedBy !== '' ? formData.orderedBy : null,
      formData.status !== '' ? formData.status : null,
      formData.despatchLocation !== '' ? formData.despatchLocation : null,
      toClientIds(formData.clientName)
    );

    triggerFileDownload(response);
  };

  const fetchTATDashboardData = async (filters: typeof formData) => {
    const effectiveFromDate = filters.fromDate || defaultFromDate;
    const effectiveToDate = filters.toDate || today;
    const apiFromDate = moment(effectiveFromDate, 'YYYY-MM-DD', true).format('YYYY-MM-DD');
    const apiToDate = moment(effectiveToDate, 'YYYY-MM-DD', true).format('YYYY-MM-DD');
    const response = await getTATDashboardDetails(
      apiFromDate,
      apiToDate,
      filters.orderedBy !== '' ? filters.orderedBy : null,
      filters.status !== '' ? filters.status : null,
      filters.despatchLocation !== '' ? filters.despatchLocation : null,
      toClientIds(filters.clientName)
    );

    if (response?.data && Array.isArray(response.data)) {
      const mappedTatData = response.data.map((item: any) => {
        const despatchLocation = String(item?.despatchLocation || '').trim() || 'Unknown';
        const parsedTat = Number.parseFloat(String(item?.tat ?? ''));

        return {
          despatchLocation,
          tat: Number.isFinite(parsedTat) ? parsedTat : 0
        };
      });

      setTatChartData(mappedTatData);
      return;
    }

    setTatChartData([]);
  };

  // Client filtering is done by the API through clientIds
  const filteredBottleOrders = bottleOrders;

  const asmOrderBarData = useMemo(() => {
    return filteredBottleOrders
      .filter((item) => Number(item.userType) === 1)
      .reduce<Record<string, number>>((acc, item) => {
        const name = String(item.orderByName || '').trim();
        if (!name) {
          return acc;
        }
        acc[name] = (acc[name] || 0) + 1;
        return acc;
      }, {});
  }, [filteredBottleOrders]);

  const asmOrderChartData = Object.entries(asmOrderBarData).map(([orderByName, count]) => ({ orderByName, count }));

  const despatchLocationBarData = useMemo(() => {
    return filteredBottleOrders.reduce<Record<string, number>>((acc, item) => {
      const location = String(item.despatchLocation || '').trim();
      if (!location) {
        return acc;
      }
      acc[location] = (acc[location] || 0) + 1;
      return acc;
    }, {});
  }, [filteredBottleOrders]);

  const despatchLocationChartData = Object.entries(despatchLocationBarData).map(([despatchLocation, count]) => ({ despatchLocation, count }));

  const getStatusLabel = (item: BottleOrderItem) => {
    const statusText = String(item.statusDescription || '').trim();
    if (statusText) {
      return statusText;
    }

    const statusId = Number(item.status);
    return bottleStatusOptions.find((option) => option.id === statusId)?.name || 'Unknown';
  };

  const statusTotals = useMemo(() => {
    return filteredBottleOrders.reduce<Record<string, number>>((acc, item) => {
      const statusLabel = getStatusLabel(item);
      acc[statusLabel] = (acc[statusLabel] || 0) + 1;
      return acc;
    }, {});
  }, [filteredBottleOrders]);

  const statusPieData = useMemo(() => {
    const palette = ['#003883', '#ee7d11', '#5765a6', '#41a85f', '#8e44ad', '#c0392b'];

    return Object.entries(statusTotals).map(([name, value], index) => ({
      name,
      value,
      color: palette[index % palette.length]
    }));
  }, [statusTotals]);

  const clientBarData = useMemo(() => {
    return filteredBottleOrders.reduce<Record<string, number>>((acc, item) => {
      const clientName = String(item.clientName || '').trim();
      if (!clientName) {
        return acc;
      }
      acc[clientName] = (acc[clientName] || 0) + 1;
      return acc;
    }, {});
  }, [filteredBottleOrders]);

  const clientChartData = Object.entries(clientBarData).map(([clientName, count]) => ({ clientName, count }));

  const handleSearch = async () => {
    if (isSearchingRef.current) return;
    isSearchingRef.current = true;
    setIsSearching(true);
    try {
      await Promise.all([
        fetchBottlerData(formData, 'view'),
        fetchTATDashboardData(formData)
      ]);
    } finally {
      isSearchingRef.current = false;
      setIsSearching(false);
    }
  };

  const handleClearAll = () => {
    setFormData(initialFormData);
    setSelectedClients([]);
    fetchBottlerData(initialFormData, 'view');
    fetchTATDashboardData(initialFormData);
  };

  const hasDefaultDates = formData.fromDate === defaultFromDate && formData.toDate === today;
  const hasFilterChanges = Object.keys(formData).some((key) => {
    const typedKey = key as keyof typeof formData;
    return formData[typedKey] !== initialFormData[typedKey];
  });
  const isSearchEnabled = hasDefaultDates || hasFilterChanges;

  const handleStatusChange = (event: SelectChangeEvent) => {
    setFormData((prev) => ({ ...prev, status: event.target.value }));
  };

  const renderNoDataFallback = (title: string) => (
    <Box
      sx={{
        p: 3,
        border: '1px solid #e0e0e0',
        borderRadius: '6px',
        minHeight: 350,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', mb: 2 }}>
        {title}
      </Typography>
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="h6">
          No data found.
        </Typography>
      </Box>
    </Box>
  );

  const renderLoadingFallback = () => (
    <Box
      sx={{
        p: 3,
        border: '1px solid #e0e0e0',
        borderRadius: '6px',
        minHeight: 350,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2 }}>
        <CircularProgress size={32} />
        <Typography variant="h6">
          Loading dashboard data...
        </Typography>
      </Box>
    </Box>
  );

  return (
    <>
      <Grid
        container
        component="form"
        spacing={2}
        sx={{ mb: 4, backgroundColor: '#f5f5f5', p: 2, borderRadius: '6px', width: '100%', ml: 0, mt: 0 }}
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          if (isSearchEnabled) handleSearch();
        }}
      >
        <Grid item xs={12} sm={6} md={3}>
          <DateInputField
            fullWidth
            label="From Date"
            value={formData.fromDate}
            onChange={(value) => setFormData((prev) => ({ ...prev, fromDate: value }))}
            max={formData.toDate || today}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DateInputField
            fullWidth
            label="To Date"
            value={formData.toDate}
            onChange={(value) => setFormData((prev) => ({ ...prev, toDate: value }))}
            min={formData.fromDate}
            max={today}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ClientMultiSelect label="Client Name" value={selectedClients} onChange={handleClientChange} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel sx={{ fontSize: "12px" }}>Status</InputLabel>
            <Select value={formData.status} label="Status" onChange={handleStatusChange} sx={{ fontSize: "12px" }}>
              {bottleStatusOptions.map((status) => (
                <MenuItem key={status.id} value={String(status.id)} sx={{ fontSize: "12px" }}>
                  {status.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel sx={{ fontSize: '12px' }}>Dispatch Location</InputLabel>
            <Select
              value={formData.despatchLocation}
              label="Dispatch Location"
              onChange={(event) => setFormData((prev) => ({ ...prev, despatchLocation: event.target.value }))}
              sx={{ fontSize: '12px' }}
            >
              {despatchLocations.map((location: any) => (
                <MenuItem key={location.name} value={location.name} sx={{ fontSize: '12px' }}>
                  {location.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            size="small"
            label="Ordered By"
            name="orderedBy"
            value={formData.orderedBy}
            onChange={(event) => setFormData((prev) => ({ ...prev, orderedBy: event.target.value }))}
            InputLabelProps={{ sx: { fontSize: '12px' } }}
            sx={{
              '& .MuiInputBase-input': {
                fontSize: '12px'
              }
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-start' }, gap: 1, width: '100%' }}>
            <Button type="button" variant="contained" disabled={!isSearchEnabled || isSearching} onClick={handleSearch}>Search</Button>
            <Button type="button" variant="outlined" onClick={handleClearAll}>Clear All</Button>
          </Box>
        </Grid>
      </Grid>

      {isLoading ? (
        renderLoadingFallback()
      ) : hasFetchedData && filteredBottleOrders.length === 0 ? (
        <Box sx={{ p: 3, textAlign: 'center', border: '1px solid #ccc', borderRadius: '6px' }}>
          <Typography variant="h6">
            No data found.
          </Typography>
        </Box>
      ) : (
        <>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                title="Requested"
                count={statusTotals.Requested || 0}
                color="#003883"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                title="Awaiting Fulfilment"
                count={statusTotals['AwaitingFulfilment'] || 0}
                color="#5765a6"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                title="Total Bottle Orders"
                count={filteredBottleOrders.length}
                color="#ee7d11"
              />
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
            <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownloadReport}>
              Download Report
            </Button>
          </Box>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6} mb={2}>
              {asmOrderChartData.length > 0 ? (
                <DashboardBarChartCard
                  title="Order By ASM"
                  data={asmOrderChartData}
                  xDataKey="orderByName"
                  barName="Orders by ASM"
                  barColor="#003883"
                  barCategoryGap="20%"
                  xAxisAngle={-20}
                  xAxisHeight={80}
                />
              ) : renderNoDataFallback('Order By ASM')}
            </Grid>

            <Grid item xs={12} md={6} mb={2}>
              {despatchLocationChartData.length > 0 ? (
                <DashboardBarChartCard
                  title="Order by Dispatch Location"
                  data={despatchLocationChartData}
                  xDataKey="despatchLocation"
                  barName="Orders by Dispatch Location"
                  barColor="#5765a6"
                  barCategoryGap="20%"
                  xAxisAngle={-20}
                  xAxisHeight={80}
                />
              ) : renderNoDataFallback('Order by Dispatch Location')}
            </Grid>

            <Grid item xs={12} md={6} mb={2}>
              {clientChartData.length > 0 ? (
                <DashboardBarChartCard
                  title="Order by Client"
                  data={clientChartData}
                  xDataKey="clientName"
                  barName="Orders by Client"
                  barColor="#ee7d11"
                  barCategoryGap="20%"
                  xAxisAngle={-20}
                  xAxisHeight={80}
                />
              ) : renderNoDataFallback('Order by Client')}
            </Grid>

            <Grid item xs={12} md={6} mb={2}>
              {statusPieData.length > 0 ? (
                <DashboardPieChartCard
                  title="Order Status"
                  data={statusPieData}
                  outerRadius={120}
                />
              ) : renderNoDataFallback('Order Status')}
            </Grid>
          </Grid>
          <Grid container spacing={3} mt={3}>
            <Grid item xs={12} md={6} sm={6} mb={2}>
              {tatChartData.length > 0 ? (
                <DashboardBarChartCard
                  title="TAT Dashboard"
                  data={tatChartData}
                  xDataKey="despatchLocation"
                  barDataKey="tat"
                  barName="TAT (hrs)"
                  barColor="#1f7a8c"
                  barCategoryGap="25%"
                  xAxisAngle={-20}
                  xAxisHeight={80}
                />
              ) : (
                renderNoDataFallback('TAT Dashboard')
              )}
            </Grid>
          </Grid>
        </>
      )
      }
    </>
  );
}
